import { Sparkles, ShieldCheck, AlertCircle, RefreshCw } from "lucide-react";
import { useState } from "react";

export const AIInsightCard = ({ title = "AI İçgörüsü", insight, loading, accent = "sky", onRetry }) => {
  const tone = accent === "amber"
    ? "from-amber-500/10 via-amber-500/5 to-transparent border-amber-500/30"
    : "from-sky-500/10 via-sky-500/5 to-transparent border-sky-500/30";

  // Detect failure: insight is null/undefined while not loading, or only contains 'raw' field with error text
  const failed = !loading && (
    insight === null ||
    insight === undefined ||
    (typeof insight === "string" && insight.toLowerCase().includes("alınamadı")) ||
    (insight && typeof insight === "object" && Object.keys(insight).length === 0)
  );

  return (
    <div
      data-testid="ai-insight-card"
      className={`relative rounded-2xl border bg-gradient-to-br ${tone} p-5`}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-sky-500/20 border border-sky-500/40 flex items-center justify-center ai-pulse">
          <Sparkles className="w-3.5 h-3.5 text-sky-600" />
        </div>
        <span className="label-eyebrow text-sky-600">{title}</span>
        <span className="ml-auto text-[0.65rem] text-slate-500 inline-flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" /> Açıklanabilir
        </span>
      </div>
      {loading ? (
        <div className="space-y-2">
          <div className="h-3 bg-slate-200/60 rounded animate-pulse w-3/4" />
          <div className="h-3 bg-slate-200/60 rounded animate-pulse w-5/6" />
          <div className="h-3 bg-slate-200/60 rounded animate-pulse w-1/2" />
          <p className="text-[0.7rem] text-slate-500 pt-2">Plena AI analizi hazırlıyor…</p>
        </div>
      ) : failed ? (
        <FailedState onRetry={onRetry} />
      ) : (
        <div className="text-sm text-slate-800 leading-relaxed">
          {typeof insight === "string" ? insight : <PrettyInsight data={insight} />}
        </div>
      )}
    </div>
  );
};

const FailedState = ({ onRetry }) => {
  const [retrying, setRetrying] = useState(false);
  const handle = async () => {
    if (!onRetry) return;
    setRetrying(true);
    try { await onRetry(); } finally { setRetrying(false); }
  };
  return (
    <div className="flex items-start gap-3" data-testid="ai-insight-fallback">
      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-800">
          AI içgörüsü şu anda hazırlanamıyor.
        </p>
        <p className="text-[0.7rem] text-slate-500 mt-1 leading-relaxed">
          Veriler hazır — yorumu birazdan tekrar deneyebilirsiniz. Bu, modelin geçici olarak yoğun olduğu anlamına gelir.
        </p>
        {onRetry && (
          <button
            data-testid="ai-retry-btn"
            onClick={handle}
            disabled={retrying}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-sky-500/30 bg-sky-500/10 text-sky-700 hover:bg-sky-500/20 transition disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${retrying ? "animate-spin" : ""}`} />
            {retrying ? "Yeniden deneniyor…" : "Tekrar dene"}
          </button>
        )}
      </div>
    </div>
  );
};

const PrettyInsight = ({ data }) => {
  if (!data) return null;
  // legacy: raw field means parsing fell back; treat as failure visually
  if (data.raw) {
    return <FailedState />;
  }
  return (
    <div className="space-y-3">
      {data.summary && <p>{data.summary}</p>}
      {data.recommended_path && (
        <p>
          <span className="text-sky-600 font-medium">Önerilen yol:</span> {data.recommended_path}
        </p>
      )}
      {data.reasoning && <p className="text-slate-700">{data.reasoning}</p>}
      {data.match_reasoning && <p>{data.match_reasoning}</p>}
      {Array.isArray(data.evidence) && data.evidence.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {data.evidence.map((e, i) => (
            <span key={i} className="evidence-chip">{e}</span>
          ))}
        </div>
      )}
      {Array.isArray(data.actions) && (
        <ul className="space-y-2 pt-1">
          {data.actions.map((a, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-sky-400 flex-shrink-0" />
              <div>
                <span className="text-slate-900 font-medium">{a.title}</span>
                {a.type && <span className="ml-2 text-[0.65rem] uppercase tracking-wider text-amber-600">{a.type}</span>}
                {a.rationale && <p className="text-xs text-slate-600 mt-0.5">{a.rationale}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {Array.isArray(data.discussion_topics) && data.discussion_topics.length > 0 && (
        <div className="pt-1">
          <div className="label-eyebrow mb-1.5">İlk Görüşme Konuları</div>
          <ul className="space-y-1">
            {data.discussion_topics.map((t, i) => (
              <li key={i} className="text-xs text-slate-700">• {t}</li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(data.strategy) && (
        <div>
          <div className="label-eyebrow mb-1.5 text-emerald-600">Strateji</div>
          <ul className="space-y-1">
            {data.strategy.map((s, i) => (
              <li key={i} className="text-xs text-slate-800">→ {s}</li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(data.risks) && (
        <div>
          <div className="label-eyebrow mb-1.5 text-rose-600">Riskler</div>
          <ul className="space-y-1">
            {data.risks.map((r, i) => (
              <li key={i} className="text-xs text-slate-700">! {r}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AIInsightCard;
